/**
 * API stubs for PSI+ chatbot admin panel.
 *
 * Backend endpoints to implement:
 *   POST /v1/admin/auth/login              { email, password } → { token }
 *   POST /v1/admin/auth/forgot-password    { email } → { success }
 *   POST /v1/admin/auth/reset-password     { reset_token, new_password } → { success }
 *   POST /v1/psi-chatbot/questions         { psychologist_id, psychologist_name, question, answer, quarter }
 *   GET  /v1/admin/psi-chatbot/questions   (admin Bearer) → { items, total, page, per_page }
 *
 * Until the backend is live, all calls fall back to localStorage.
 */

import { apiPost } from '../http';
import { API_CORE_URL } from '../config';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChatbotQuestion {
  id: string;
  psychologist_id: string;
  psychologist_name: string;
  question: string;
  answer: string;
  quarter: string;
  created_at: string;
}

export interface AdminLoginResponse {
  token: string;
}

// ─── Storage keys ─────────────────────────────────────────────────────────────

const LS_QUESTIONS_KEY = 'psi-chatbot-questions';
const LS_ADMIN_TOKEN_KEY = 'psi-admin-token';

// ─── Admin auth ───────────────────────────────────────────────────────────────

export async function adminLogin(email: string, password: string): Promise<{ token: string }> {
  const resp = await apiPost<{ data: AdminLoginResponse }>('/v1/admin/auth/login', { email, password });
  return { token: (resp as { data: AdminLoginResponse }).data.token };
}

export async function adminForgotPassword(email: string): Promise<void> {
  await apiPost('/v1/admin/auth/forgot-password', { email });
}

export async function adminResetPassword(reset_token: string, new_password: string): Promise<void> {
  await apiPost('/v1/admin/auth/reset-password', { reset_token, new_password });
}

// Direct fetch for admin-authenticated calls (bypasses psychologist JWT injection)
async function adminFetch<T>(path: string, adminToken: string, params?: Record<string, unknown>): Promise<T> {
  const usp = new URLSearchParams();
  if (params) Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null) usp.set(k, String(v)); });
  const qs = usp.toString() ? `?${usp.toString()}` : '';
  const resp = await fetch(`${API_CORE_URL}${path}${qs}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
  });
  if (!resp.ok) throw new Error(`Error ${resp.status}`);
  return resp.json() as Promise<T>;
}

export function getAdminToken(): string | null {
  return localStorage.getItem(LS_ADMIN_TOKEN_KEY);
}

export function saveAdminToken(token: string): void {
  localStorage.setItem(LS_ADMIN_TOKEN_KEY, token);
}

export function clearAdminToken(): void {
  localStorage.removeItem(LS_ADMIN_TOKEN_KEY);
}

export function isAdminTokenValid(): boolean {
  const token = getAdminToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp > Date.now() / 1000;
  } catch {
    return false;
  }
}

// ─── Question logging ─────────────────────────────────────────────────────────

export interface LogQuestionPayload {
  psychologist_id: string;
  psychologist_name: string;
  question: string;
  answer: string;
  quarter: string;
}

export async function logChatbotQuestion(payload: LogQuestionPayload): Promise<void> {
  // Try backend first; on any failure, fall back to localStorage
  try {
    await apiPost('/v1/psi-chatbot/questions', payload);
  } catch {
    saveQuestionToLocalStorage(payload);
  }
}

// ─── Admin: fetch questions ───────────────────────────────────────────────────

export interface GetQuestionsParams {
  page?: number;
  per_page?: number;
  search?: string;
  from?: string;
  to?: string;
}

export interface QuestionsResponse {
  items: ChatbotQuestion[];
  total: number;
  page: number;
  per_page: number;
}

export async function getAdminChatbotQuestions(
  adminToken: string,
  params: GetQuestionsParams = {},
): Promise<QuestionsResponse> {
  const resp = await adminFetch<{ data: QuestionsResponse }>(
    '/v1/admin/psi-chatbot/questions',
    adminToken,
    params as Record<string, unknown>,
  );
  return resp.data;
}

// ─── localStorage fallback ────────────────────────────────────────────────────

function saveQuestionToLocalStorage(payload: LogQuestionPayload): void {
  const stored = getLocalStorageQuestions();
  const entry: ChatbotQuestion = {
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    ...payload,
    created_at: new Date().toISOString(),
  };
  stored.push(entry);
  // Keep last 500 entries
  const trimmed = stored.slice(-500);
  localStorage.setItem(LS_QUESTIONS_KEY, JSON.stringify(trimmed));
}

export function getLocalStorageQuestions(): ChatbotQuestion[] {
  try {
    return JSON.parse(localStorage.getItem(LS_QUESTIONS_KEY) ?? '[]') as ChatbotQuestion[];
  } catch {
    return [];
  }
}

export function clearLocalStorageQuestions(): void {
  localStorage.removeItem(LS_QUESTIONS_KEY);
}
